/* Copyright (c) 2020-present CloudIO Inc */

import {
  chartDatalabelsAlign,
  chartDatalabelsAnchor,
  chartTypes,
} from 'componentregistry/registry/constants';
import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { page as SamplePage } from '../../help/sample_pages/ChartPage';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './Chartjs';
import { chartAnalyze } from './ChartAnalyze';
import { backgroundColors, borderColors } from 'components/Chartjs/ChartjsLazy';

const ChartjsManager: ComponentManagerFn<'Chartjs'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'Chartjs'>(platform, 'Chartjs'),
    type: 'Chartjs',
    name: 'Chart',
    isChildAllowed: (
      page,
      item,
      childType,
      { appUid, itemId, pageId, platform: p },
    ) => {
      const allowed = (
        [
          'ChartJSTooltip',
          'EventSubscription',
          'ExpressionWatchListener',
          'PulseTip',
          'TriggerOnClick',
        ] as ItemType[]
      ).includes(childType);

      const parent = p.findParentOfType(
        appUid,
        pageId,
        itemId,
        'CombinationChart',
      );
      if (parent && allowed) {
        return 'Not allowed when Chart is inside Combination Chart.';
      }
      return allowed;
    },
    defaultValue,
    props: {
      title: {
        label: 'Title',
        type: 'expression',
        returnType: async () => 'string',
      },
      chartType: {
        label: 'Chart Type',
        options: async (item, { appUid, itemId, pageId, platform: p }) => {
          const parent = p.findParentOfType(
            appUid,
            pageId,
            itemId,
            'CombinationChart',
          );
          if (parent) {
            return toOptions(['bar', 'line']);
          } else {
            return chartTypes;
          }
        },
        type: 'radio',
      },
      indexAxis: {
        label: 'Bar Direction',
        options: { x: 'Vertical', y: 'Horizontal' },
        type: 'select',
      },
      stackedBars: {
        label: 'Stacked Bars',
        type: 'boolean',
      },
      backgroundColor: {
        label: 'BG Color',
        type: 'BGColor',
        category: 'Look & Feel',
        allowCustomColor: true,
      },
      showDownloadOptions: {
        label: 'Show Download Option',
        type: 'boolean',
      },
      fileName: {
        label: 'File Name',
        type: 'string',
      },
      gridLines: {
        label: 'Grid Lines',
        options: {
          x: 'X Axis Only',
          y: 'Y Axis Only',
          xy: 'Both X & Y Axis',
          none: 'None',
        },
        type: 'select',
        required: true,
      },
      ticks: {
        label: 'Axis Labels',
        options: {
          x: 'X Axis Only',
          y: 'Y Axis Only',
          xy: 'Both X & Y Axis',
          none: 'None',
        },
        type: 'select',
        required: true,
      },
      xAxisLabel: {
        label: 'X Axis Label',
        type: 'expression',
        returnType: async () => 'string',
      },
      yAxisLabel: {
        label: 'Y Axis Label',
        type: 'expression',
        returnType: async () => 'string',
      },
      legendPosition: {
        label: 'Legend Position',
        options: toOptions(['left', 'top', 'right', 'bottom', 'hidden']),
        type: 'radio',
      },
      showBorder: {
        label: 'Show Borders',
        type: 'boolean',
      },
      fill: {
        label: 'Fill',
        type: 'boolean',
      },
      colorPallet: {
        label: 'Color Palette',
        type: 'expression',
        returnType: async () => 'any',
        helpText: `Comma seperated HTML Color codes in hex or rgb or rgba.

Default: ${backgroundColors.join(', ')}`,
      },
      borderColorPallet: {
        label: 'Border Color Palette',
        type: 'expression',
        returnType: async () => 'any',
        helpText: `Comma seperated HTML Color codes in hex or rgb or rgba.

Default: ${borderColors.join(', ')}`,
      },
      dsalias: {
        category: 'Data',
        label: 'DataSource Alias',
        required: true,
        type: 'DataSourceAliasChooser',
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
        ],
        helpText: 'Use Query Chart action to query this externally.',
      },
      categoryAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Category Attribute',
        type: 'ComboAttributeChooser',
      },
      categoryAttributeLookup: {
        category: 'Data',
        label: 'Category Attribute Lookup',
        type: 'LookupCodeChooser',
      },
      labelAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Label Attribute',
        required: true,
        type: 'ComboAttributeChooser',
      },
      labelAttributeLookup: {
        category: 'Data',
        label: 'Label Attribute Lookup',
        type: 'LookupCodeChooser',
      },
      labelFormatExpression: {
        label: 'Formated Label Expression',
        type: 'expressionOnly',
        returnType: () => 'string',
        category: 'Data',
        helpText:
          'Use this expression to format the label attribute value. You can use $._.label to access the label value inside the expression and $._.record to access the entire record.',
      },
      valueAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Value Attribute',
        required: true,
        type: 'ComboAttributeChooser',
      },
      orderByAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Order By Attribute',
        type: 'ComboAttributeChooser',
      },
      functionType: {
        category: 'Data',
        label: 'Value Function',
        options: toOptions(['Avg', 'Count', 'Max', 'Min', 'Sum']),
        required: true,
        type: 'select',
      },
      doNotPrefetch: {
        label: 'Do Not Prefetch',
        type: 'boolean',
        category: 'Data',
      },
      yAxisPosition: {
        label: 'Y Axis Position',
        options: toOptions(['left', 'right']),
        type: 'select',
      },
      showMaxLine: {
        label: 'Show Max Line',
        type: 'boolean',
        category: 'Data',
      },
      showMinLine: {
        label: 'Show Min Line',
        type: 'boolean',
        category: 'Data',
      },
      showAvgLine: {
        label: 'Show Avg Line',
        type: 'boolean',
        category: 'Data',
      },
      avgLineColor: {
        label: 'Avg Line Color',
        type: 'ColorExpression',
        category: 'Data',
      },
      avgLineWidth: {
        label: 'Avg Line Width',
        type: 'number',
        category: 'Data',
      },
      hideTooltips: {
        label: 'Hide Tooltips',
        type: 'boolean',
        category: 'Data',
      },
      skipNullValue: {
        label: 'Exclude Empty Bars',
        type: 'boolean',
        category: 'Data',
        helpText:
          'Enable this option to exclude data points with a value of 0  from being rendered in the chart.',
      },
      chartWidth: {
        label: 'Width',
        max: 800,
        min: 0,
        step: 40,
        type: 'number',
        helpText:
          "Setting a value 1 or less will be considered as percentage of the parent container's width. i.e. a value of 1 takes 100% of the container's width.",
      },
      chartHeight: {
        label: 'Height',
        max: 800,
        min: 0,
        step: 40,
        type: 'number',
        helpText:
          "Setting a value 1 or less will be considered as percentage of the parent container's height. i.e. a value of 1 takes 100% of the container's height.",
      },
      preRenderFunction: {
        label: 'Pre-render Callback',
        type: 'controllerFunction',
        category: 'Controllers',
        alwaysShow: true,
        helpText:
          'Use this callback function to customize the chart by manipulating the `data` & `options` passed to this function. Refer https://www.chartjs.org/docs/latest/configuration/ for various options you could use to customize the charts.',
      },
      enableDatalabels: {
        label: 'Enable Datalabels',
        type: 'boolean',
        category: 'Datalabels',
        helpText: 'Enable this to display data labels on chart element.',
      },
      anchor: {
        label: 'Anchor',
        type: 'select',
        options: chartDatalabelsAnchor,
        category: 'Datalabels',
        helpText: `An anchor point is to positon the data label on the chart element defined by the orientation.
        Check this https://chartjs-plugin-datalabels.netlify.app/guide/positioning.html#anchoring for more info.`,
      },
      align: {
        label: 'Align',
        type: 'select',
        options: chartDatalabelsAlign,
        category: 'Datalabels',
        helpText: `The align option defines the position of the label relative to the anchor point position and orientation.
        Check this https://chartjs-plugin-datalabels.netlify.app/guide/positioning.html#alignment-and-offset for more info.`,
      },
      offset: {
        label: 'Offset',
        type: 'number',
        category: 'Datalabels',
        helpText: `The offset represents the distance (in pixels) to pull the label away from the anchor point. 
        This option is not applicable when align is 'center'. 
        Also note that if align is 'start', the label is moved in the opposite direction. The default value is 4.
        Check this https://chartjs-plugin-datalabels.netlify.app/guide/positioning.html#alignment-and-offset for more info.`,
      },
      dlColorPallet: {
        label: 'Datalabels Color Palette',
        type: 'expression',
        category: 'Datalabels',
        helpText: 'Comma seperated HTML Color codes in hex or rgb or rgba.',
        returnType: async () => 'string',
      },
    },
    isPropertyVisible(
      prop,
      itemData,
      { appUid, itemId, pageId, platform: p },
    ): boolean {
      const parent = p.findParentOfType(
        appUid,
        pageId,
        itemId,
        'CombinationChart',
      );
      if (parent) {
        switch (prop) {
          case 'yAxisPosition':
            return true;
          case 'chartHeight':
          case 'chartWidth':
          case 'gridLines':
          case 'legendPosition':
          case 'preRenderFunction':
          case 'ticks':
          case 'title':
          case 'xAxisLabel':
          case 'yAxisLabel':
          case 'showMaxLine':
          case 'showMinLine':
          case 'labelFormatExpression':
          case 'hideTooltips':
          case 'showAvgLine':
          case 'align':
          case 'anchor':
          case 'dlColorPallet':
          case 'enableDatalabels':
          case 'fill':
          case 'offset':
            return false;
          default:
        }
      }

      switch (prop) {
        case 'avgLineColor':
        case 'avgLineWidth':
          return itemData.showAvgLine;
        case 'yAxisPosition':
          return false;
        case 'indexAxis':
        case 'stackedBars':
          return itemData.chartType === 'bar';
        case 'categoryAttribute':
        case 'labelAttribute':
        case 'orderByAttribute':
        case 'valueAttribute':
        case 'functionType':
          return !!itemData.dsalias;
        case 'borderColorPallet':
          return itemData.showBorder;
        case 'legendPosition':
          return (
            !!itemData.categoryAttribute ||
            !['bar', 'line', 'radar'].includes(itemData.chartType)
          );
        case 'align':
        case 'anchor':
        case 'fill':
        case 'dlColorPallet':
          return itemData.enableDatalabels;
        case 'offset':
          return itemData.enableDatalabels && itemData.align !== 'center';
        case 'fileName':
          return itemData.showDownloadOptions;
        case 'skipNullValue':
          return (
            !!itemData.categoryAttribute ||
            !!itemData.categoryAttributeLookup.code
          );
        case 'showAvgLine':
        case 'showMinLine':
        case 'showMaxLine':
        default:
          return true;
      }
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV'
        ? { aliasProp: 'dsalias', embeddedPageIdProp: 'embeddedPageId' } // TODO: Add embeddedPageId prop
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.dsalias === alias,
    getDisplayText: (item) => item.props.labelAttribute ?? '',
    category: 'Dashboard',
    icon: (item) => {
      const { chartType, fill } = item.props;
      return {
        iconName:
          chartType === 'bar'
            ? 'chart-bar'
            : chartType === 'pie'
            ? 'chart-pie'
            : chartType === 'line' && fill
            ? 'chart-area'
            : 'chart-line',
      };
    },
    analyze: chartAnalyze,
    canHaveChildren: () => true,
    appendOverlay: true,
    description:
      'Chart is a graphical representation of data, in which the data is represented by symbols, such as bars in a bar chart, lines in a line chart. A chart can represent tabular numeric data, functions or some kinds of qualitative structure and provides different info',
    helpRenders: [
      {
        title: 'Basic Chart',
        description:
          'Choose a Chart Type to render and Legend Position if applicable.',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        height: 280,
        visibleProps: ['legendPosition', 'chartType'],
        item: {
          children: [],
          itemId: 'chartjs',
          parent: 'flexItem1',
          props: {
            dsalias: 'test',
            functionType: 'Avg',
            labelAttribute: 'name',
            legendPosition: 'left',
            valueAttribute: 'someNumber',
          },
          type: 'Chartjs',
        },
      },
      {
        title: 'Custom Color Palette',
        description: 'You can specify your own colors.',
        page: SamplePage,
        exampleId: 'e2',
        clearParentChildren: true,
        visibleProps: ['colorPallet', 'borderColorPallet'],
        item: {
          children: [],
          itemId: 'chartjs',
          parent: 'flexItem1',
          props: {
            dsalias: 'test',
            functionType: 'Avg',
            labelAttribute: 'name',
            legendPosition: 'left',
            valueAttribute: 'someNumber',
            colorPallet: { type: 'text', value: 'gray,orange,pink' },
            borderColorPallet: { type: 'text', value: 'gray,orange,pink' },
          },
          type: 'Chartjs',
        },
      },
      {
        title: 'Grid Lines & Axis Labels',
        page: SamplePage,
        exampleId: 'e3',
        clearParentChildren: true,
        visibleProps: ['gridLines', 'ticks'],
        item: {
          children: [],
          itemId: 'chartjs',
          parent: 'flexItem1',
          props: {
            dsalias: 'test',
            functionType: 'Avg',
            labelAttribute: 'name',
            legendPosition: 'left',
            valueAttribute: 'someNumber',
            gridLines: 'none',
            ticks: 'none',
          },
          type: 'Chartjs',
        },
      },
    ],
  };
};

export default ChartjsManager;
